(() => {
  if (window.NativeVariantImagePreview) return;

  const choiceSelector = '[data-native-variant-image-choice][data-preview-src]';
  const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const preview = document.createElement('div');
  preview.className = 'native-variant-image-preview';
  preview.setAttribute('aria-hidden', 'true');
  preview.innerHTML = `
    <img class="native-variant-image-preview__image" alt="">
    <span class="native-variant-image-preview__caption">
      <span class="native-variant-image-preview__label"></span>
      <span class="native-variant-image-preview__recommendation" role="img" aria-label="Most Popular" hidden>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12 22c4.97 0 9-3.58 9-8 0-1.95-.86-3.92-2.3-5.4.1 2.4-1.4 3.3-2.2 3.4.7-4.2-2.3-8.1-6.5-10 1.8 3.1 1.3 6.4-.5 8.5-1.1-1.4-1.3-3.1-1.2-4.3C5.8 8 3 11 3 14c0 4.42 4.03 8 9 8ZM12.1 18.8c-1.5 0-2.7-1.12-2.7-2.5 0-1.05.55-1.9 1.55-3.05.08.9.4 1.58.9 2.05.62-.78.97-1.76.82-2.92 1.34.98 2.13 2.36 2.13 3.88 0 1.42-1.2 2.54-2.7 2.54Z"
          />
        </svg>
      </span>
    </span>
  `;
  document.body.appendChild(preview);

  const previewImage = preview.querySelector('.native-variant-image-preview__image');
  const previewLabel = preview.querySelector('.native-variant-image-preview__label');
  const previewRecommendation = preview.querySelector('.native-variant-image-preview__recommendation');
  let activeChoice = null;
  let hideTimer = 0;

  const parseVariantMedia = (picker) => {
    if (picker.nativeVariantMedia) return picker.nativeVariantMedia;

    const dataElement = picker.querySelector('[data-native-variant-media-data]');
    try {
      picker.nativeVariantMedia = JSON.parse(dataElement?.textContent || '[]');
    } catch (error) {
      picker.nativeVariantMedia = [];
      console.warn('Unable to read native variant image data.', error);
    }

    return picker.nativeVariantMedia;
  };

  const getSelectedOptions = (picker) =>
    Array.from(picker.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') return element.value;
      return element.querySelector('input[type="radio"]:checked')?.value;
    });

  const findMediaForValue = (variantMedia, selectedOptions, optionIndex, value) => {
    const candidates = variantMedia.filter((variant) => {
      if (!variant.cardSrc || variant.options?.[optionIndex] !== value) return false;

      return selectedOptions
        .slice(0, optionIndex)
        .every((selectedValue, index) => !selectedValue || variant.options?.[index] === selectedValue);
    });

    if (!candidates.length) return null;

    const laterOptionsMatch = (variant) =>
      selectedOptions
        .slice(optionIndex + 1)
        .every((selectedValue, offset) =>
          !selectedValue || variant.options?.[optionIndex + offset + 1] === selectedValue
        );

    return (
      candidates.find((variant) => variant.available && laterOptionsMatch(variant)) ||
      candidates.find(laterOptionsMatch) ||
      candidates.find((variant) => variant.available) ||
      candidates[0]
    );
  };

  const showPlaceholder = (choice) => {
    choice.removeAttribute('data-preview-src');
    choice.removeAttribute('data-card-src');
    delete choice.dataset.pendingCardSrc;

    const mediaContainer = choice.querySelector('.variant-image-card__media');
    if (!mediaContainer || mediaContainer.querySelector('.variant-image-card__placeholder')) return;

    const placeholder = document.createElement('span');
    placeholder.className = 'variant-image-card__placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    mediaContainer.replaceChildren(placeholder);
  };

  const commitChoiceImage = (choice, media) => {
    if (choice.dataset.pendingCardSrc !== media.cardSrc) return;

    const mediaContainer = choice.querySelector('.variant-image-card__media');
    if (!mediaContainer) return;

    let image = mediaContainer.querySelector('.variant-image-card__image');
    if (!image) {
      image = document.createElement('img');
      image.className = 'variant-image-card__image';
      image.loading = 'lazy';
      mediaContainer.replaceChildren(image);
    }

    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = media.cardSrc;
    image.alt = media.alt || choice.dataset.previewLabel || '';
    choice.dataset.cardSrc = media.cardSrc;
    choice.dataset.previewSrc = media.previewSrc || media.cardSrc;
    delete choice.dataset.pendingCardSrc;
  };

  const updateChoiceImage = (choice, media) => {
    if (!media) {
      showPlaceholder(choice);
      return;
    }

    if (choice.dataset.cardSrc === media.cardSrc) {
      choice.dataset.previewSrc = media.previewSrc || media.cardSrc;
      return;
    }

    choice.dataset.pendingCardSrc = media.cardSrc;
    const preloader = new Image();
    preloader.onload = () => commitChoiceImage(choice, media);
    preloader.onerror = () => {
      if (choice.dataset.pendingCardSrc === media.cardSrc) showPlaceholder(choice);
    };
    preloader.src = media.cardSrc;
  };

  const syncDependentVariantImages = (picker) => {
    const variantMedia = parseVariantMedia(picker);
    if (!variantMedia.length) return;

    const selectedOptions = getSelectedOptions(picker);
    picker.querySelectorAll('[data-native-variant-image-option]').forEach((group) => {
      const optionIndex = Number(group.dataset.nativeVariantImageOption) - 1;
      if (optionIndex < 1) return;

      group.querySelectorAll('[data-native-variant-image-choice]').forEach((choice) => {
        const input = document.getElementById(choice.htmlFor);
        if (!input) return;

        const media = findMediaForValue(variantMedia, selectedOptions, optionIndex, input.value);
        updateChoiceImage(choice, media);
      });
    });
  };

  const scheduleDependentImageSync = (picker) => {
    window.requestAnimationFrame(() => syncDependentVariantImages(picker));
  };

  const clearHideTimer = () => {
    window.clearTimeout(hideTimer);
    hideTimer = 0;
  };

  const hide = (delay = 0) => {
    clearHideTimer();
    hideTimer = window.setTimeout(() => {
      preview.classList.remove('is-visible', 'is-touch');
      preview.setAttribute('aria-hidden', 'true');
      activeChoice = null;
    }, delay);
  };

  const positionDesktopPreview = (choice) => {
    preview.classList.remove('is-touch');
    preview.style.left = '0px';
    preview.style.top = '0px';
    preview.classList.add('is-visible');

    const choiceRect = choice.getBoundingClientRect();
    const previewRect = preview.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 12;
    const fitsAbove = choiceRect.top >= previewRect.height + gap + viewportPadding;
    const placement = fitsAbove ? 'top' : 'bottom';
    const top = fitsAbove
      ? choiceRect.top - previewRect.height - gap
      : Math.min(choiceRect.bottom + gap, window.innerHeight - previewRect.height - viewportPadding);
    const idealLeft = choiceRect.left + choiceRect.width / 2 - previewRect.width / 2;
    const left = Math.max(
      viewportPadding,
      Math.min(idealLeft, window.innerWidth - previewRect.width - viewportPadding)
    );
    const anchorX = Math.max(
      16,
      Math.min(choiceRect.left + choiceRect.width / 2 - left, previewRect.width - 16)
    );

    preview.dataset.placement = placement;
    preview.style.left = `${left}px`;
    preview.style.top = `${Math.max(viewportPadding, top)}px`;
    preview.style.setProperty('--preview-anchor-x', `${anchorX}px`);
  };

  const show = (choice, { touch = false } = {}) => {
    if (!choice?.dataset.previewSrc) return;

    clearHideTimer();
    activeChoice = choice;
    previewImage.src = choice.dataset.previewSrc;
    previewImage.alt = choice.dataset.previewLabel || '';
    previewLabel.textContent = choice.dataset.previewLabel || '';
    previewRecommendation.hidden = choice.dataset.previewRecommended !== 'most_popular';
    preview.classList.toggle('is-touch', touch);
    preview.classList.add('is-visible');
    preview.setAttribute('aria-hidden', 'false');

    if (touch) {
      preview.style.removeProperty('left');
      preview.style.removeProperty('top');
      preview.style.removeProperty('--preview-anchor-x');
    } else {
      positionDesktopPreview(choice);
    }

  };

  const getChoice = (event) => event.target.closest?.(choiceSelector);

  document.addEventListener('pointerover', (event) => {
    if (!finePointerQuery.matches || (event.pointerType && event.pointerType !== 'mouse')) return;
    const choice = getChoice(event);
    if (!choice || choice.contains(event.relatedTarget)) return;
    show(choice);
  });

  document.addEventListener('pointerout', (event) => {
    if (!finePointerQuery.matches || (event.pointerType && event.pointerType !== 'mouse')) return;
    const choice = getChoice(event);
    if (!choice || choice.contains(event.relatedTarget)) return;
    hide(100);
  });

  document.addEventListener('focusin', (event) => {
    const choice = getChoice(event);
    if (choice && finePointerQuery.matches) show(choice);
  });

  document.addEventListener('focusout', (event) => {
    if (getChoice(event) && !preview.classList.contains('is-touch')) hide(100);
  });

  document.addEventListener('click', (event) => {
    const choice = getChoice(event);
    const isAssociatedChoiceInput = event.target.matches?.(
      '.product-form__input--variant-images input[type="radio"]'
    );
    const isTouchClick = !finePointerQuery.matches || (event.pointerType && event.pointerType !== 'mouse');

    if (choice && isTouchClick) {
      show(choice, { touch: true });
      return;
    }

    if (isAssociatedChoiceInput) return;

    if (preview.classList.contains('is-touch') && !preview.contains(event.target)) {
      hide();
    }
  });

  document.addEventListener('change', (event) => {
    const picker = event.target.closest?.('variant-selects');
    if (!picker) return;
    if (activeChoice && picker.contains(activeChoice)) hide();
    scheduleDependentImageSync(picker);
  });

  document.querySelectorAll('variant-selects').forEach(syncDependentVariantImages);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('variant-selects').forEach(syncDependentVariantImages);
  });

  window.addEventListener(
    'scroll',
    () => {
      if (activeChoice && !preview.classList.contains('is-touch')) hide();
    },
    { passive: true }
  );
  window.addEventListener('resize', () => hide());

  window.NativeVariantImagePreview = { show, hide };
})();
